import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select } from "antd";
import PhoneInput from "react-phone-number-input";
import jwt_decode from "jwt-decode";
import { SyncOutlined } from "@ant-design/icons";
import axios from "axios";
import { postRequest, getRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import "react-phone-number-input/style.css";
import "./OtpLoginForm.scss";
import { toast } from "react-toastify";
import { setAuthToken, setRefreshToken } from "../../utils/helper";
import {
  setStripeOnboardingSessionFlag,
  fetchAndStoreStripeOnboardingUrl,
  fetchAccountOnboardingComplete,
} from "../../utils/stripeOnboarding";

const ERROR_MESSAGES = {
    INVALID_OTP: "Invalid OTP. please try again",
    SEND_FAILED: "Failed to send OTP. please try again",
    GENERIC: "Something went wrong. please try again",
    WRONG_ROLE: "Please login with organisation admin credentials",
};

const RESEND_COOLDOWN = 60;

export const normalizeOrgOptions = (organizations = []) =>
    organizations
        .map((item) => {
            const id = item?.id;
            const label =
                item?.name ?? "Unknown Organization";

            return id == null ? null : { value: id, label };
        })
        .filter(Boolean);

export default function OtpLoginForm() {
    const navigate = useNavigate();
    const role = "PARTNER_ADMIN";

    const [form] = Form.useForm();
    const phoneValue = Form.useWatch("phone", form);
    const otpValue = Form.useWatch("otp", form);

    const [step, setStep] = useState("phone");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [timer, setTimer] = useState(0);
    const timerRef = useRef(null);
    const [responseCode, setResponseCode] = useState(null);
    const [orgList, setOrgList] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [showOrgDropdown, setShowOrgDropdown] = useState(false);

    const partnerId = sessionStorage.getItem("partnerId");

    useEffect(() => {
        if (timer > 0) {
            timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [timer]);

    const handleSendOtp = async () => {
        try {
            setLoading(true);
            setErrorMessage("");

            const values = await form.validateFields(["phone"]);
            const trimmedPhone = (values.phone || "").trim();

            const response = await axios.post(
                userServiceApi.fetchOrgList(role),
                {
                    phoneNumber: trimmedPhone,
                }
            );

            const list = Array.isArray(response?.data?.list) ? response.data.list : [];
            const normalizedOrgList = normalizeOrgOptions(list);

            setPhone(trimmedPhone);
            setOrgList(normalizedOrgList);

            if (!list || list.length === 0) {
                setShowOrgDropdown(false);
                setSelectedOrgId(null);
                setResponseCode(null);
                toast.error(
                    "No account found with this phone number. Please enter the phone number that was used while creating the partner."
                );
                return;
            }

            if (normalizedOrgList.length === 1) {
                const singleOrg = normalizedOrgList[0];
                setSelectedOrgId(singleOrg.value);
                setShowOrgDropdown(false);
                setResponseCode(200);
                setStep("otp");
                setTimer(RESEND_COOLDOWN);
                return;
            }

            setSelectedOrgId(null);
            setShowOrgDropdown(true);
            setResponseCode(null);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data?.errorName ||
                error?.message ||
                ERROR_MESSAGES.SEND_FAILED
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOrgSelectionAndSendOtp = async () => {
        if (!selectedOrgId) {
            toast.error("Please select a partner");
            return;
        }

        try {
            setLoading(true);

            const response = await axios.post(
                userServiceApi.sendOtp(selectedOrgId, role, partnerId),
                {
                    phoneNumber: phone,
                }
            );

            if (response?.status === 200) {
                setResponseCode(200);
                setStep("otp");
                setTimer(RESEND_COOLDOWN);
            }
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                ERROR_MESSAGES.SEND_FAILED
            );
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (timer > 0 || !phone) return;

        try {
            setLoading(true);

            const orgId = selectedOrgId || orgList[0]?.value;

            if (!orgId) {
                toast.error("Partner not selected");
                return;
            }

            await axios.post(userServiceApi.sendOtp(orgId, role, partnerId), {
                phoneNumber: phone,
            });

            setTimer(RESEND_COOLDOWN);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                ERROR_MESSAGES.SEND_FAILED
            );
        } finally {
            setLoading(false);
        }
    };



    const handleVerifyOtp = async () => {
        try {
            setLoading(true);
            setErrorMessage("");
            const values = await form.validateFields(["otp"]);
            const response = await postRequest(userServiceApi.verifyOtp(selectedOrgId, role), {
                phoneNumber: phone,
                code: values?.otp,
            });

            if (response) {
                const { accessToken, refreshToken } = response;


                setAuthToken(accessToken);
                setRefreshToken(refreshToken);
                const decoded = jwt_decode(accessToken);
                sessionStorage.setItem("loginPage", "false");
                sessionStorage.setItem("OrgEmail", decoded?.email);



                if (decoded?.role === "PARTNER_ADMIN") {
                    try {
                        const headers = {
                            Authorization: `Bearer ${response?.accessToken}`,
                        };
                        const OrgResponse = await getRequest(
                            userServiceApi.partnerDetails(),
                            { headers }
                        );
                        if (OrgResponse?.data) {
                            sessionStorage.setItem("partnerId", OrgResponse.data.id);
                            sessionStorage.setItem("OrgImage", OrgResponse.data.mediaLink);
                            sessionStorage.setItem("OrgName", OrgResponse.data.subDomainName);
                            const connectedAccountId = OrgResponse.data.connectedAccountId;
                            sessionStorage.setItem("connectedAccountId", connectedAccountId);

                            let onboardingComplete = false;
                            if (connectedAccountId) {
                              onboardingComplete = await fetchAccountOnboardingComplete(
                                connectedAccountId
                              );
                              if (!onboardingComplete) {
                                await fetchAndStoreStripeOnboardingUrl(connectedAccountId);
                              }
                            } else {
                              setStripeOnboardingSessionFlag(false);
                            }

                            setLoading(false);
                            navigate(onboardingComplete ? "/partner-org" : "/onboarding");
                            return;
                        }
                    } catch (error) {
                        setLoading(false);
                        toast.error(ERROR_MESSAGES.WRONG_ROLE);
                    }
                } else {
                    setLoading(false);
                }



            }
        } catch (error) {
            toast.error(error?.message || ERROR_MESSAGES.INVALID_OTP);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setStep("phone");
        setTimer(0);
        setOrgList([]);

        setSelectedOrgId(null);
        setShowOrgDropdown(false);
        setResponseCode(null);
        form.setFieldValue("otp", undefined);
        form.resetFields(['partnerId'])
    };

    return (
        <Form
            form={form}
            layout="vertical"
            className="otp-login-form"
            requiredMark={false}
            onFinish={() => {
                if (step === "phone") handleSendOtp();
                else handleVerifyOtp();
            }}
        >
            {step === "phone" ? (
                <>
                    <Form.Item
                        className=""
                        label="Mobile Number"
                        name="phone"
                        rules={[
                            { required: true, message: "Mobile number is required" },
                            {
                                pattern: /^\+[1-9]\d{6,14}$/,
                                message: "Enter a valid mobile number",
                            },
                        ]}
                    >
                        <PhoneInput
                            className="otp-phone-input"
                            defaultCountry="GB"
                            placeholder="Enter mobile number"
                            focusInputOnCountrySelection
                            international
                            value={phoneValue || ""}
                            onChange={(value) => form.setFieldValue("phone", value || "")}
                        />
                    </Form.Item>

                    {showOrgDropdown && (
                        <>
                            <Form.Item
                                label="Select Partner"
                                name="partnerId"
                                rules={[{ required: true, message: "Please select a partner" }]}
                            >
                                <Select
                                    placeholder="Select Partner"
                                    value={selectedOrgId}
                                    onChange={(value) => {
                                        setSelectedOrgId(value);
                                        form.setFieldValue("partnerId", value);
                                    }}
                                    options={orgList}
                                    showSearch
                                    optionFilterProp="label"
                                />
                            </Form.Item>

                            <button
                                type="button"
                                className="otp-login-submit"
                                disabled={loading || !selectedOrgId}
                                onClick={handleOrgSelectionAndSendOtp}
                            >
                                {loading ? <SyncOutlined spin /> : "Send OTP"}
                            </button>
                        </>
                    )}

                    {errorMessage && <div className="otp-login-error">{errorMessage}</div>}

                    {!showOrgDropdown && (
                        <button
                            type="button"
                            className="otp-login-submit"
                            disabled={loading || !phoneValue}
                            onClick={() => {
                                setSelectedOrgId(null);
                                handleSendOtp();
                            }}
                        >
                            {loading ? <SyncOutlined spin /> : "Continue"}
                        </button>
                    )}
                </>
            ) : responseCode === 200 ? (
                <>
                    <div className="otp-sent-info">
                        OTP sent to <strong>{phone}</strong>
                        <button type="button" className="otp-change-link" onClick={handleBack}>
                            Change
                        </button>
                    </div>

                    <Form.Item
                        label="Enter OTP"
                        name="otp"
                        rules={[
                            { required: true, message: "OTP is required" },
                            {
                                len: 6,
                                pattern: /^\d{6}$/,
                                message: "OTP must be 6 digits",
                            },
                        ]}
                    >
                        <Input
                            className="otp-login-input"
                            placeholder="Enter 6-digit OTP"
                            maxLength={6}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="one-time-code"
                        />
                    </Form.Item>

                    <button
                        type="button"
                        className="otp-login-submit"
                        disabled={loading || !otpValue || otpValue.length !== 6}
                        onClick={handleVerifyOtp}
                    >
                        {loading ? <SyncOutlined spin /> : "Verify OTP"}
                    </button>

                    <div className="otp-resend">
                        {timer > 0 ? (
                            <span>Resend OTP in {timer}s</span>
                        ) : (
                            <button
                                type="button"
                                className="otp-resend-link"
                                onClick={handleResendOtp}
                                disabled={loading}
                            >
                                Resend OTP
                            </button>
                        )}
                    </div>
                </>
            ) : null}
        </Form>
    );
}
